import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";
import { capitalize } from "./CustomerForm";

const StatusPill = ({ status }) => (
  <span
    className={`coa-pill ${
      status === "active" ? "coa-pill-dr" : "coa-pill-muted"
    }`}
  >
    {capitalize(status) || "—"}
  </span>
);

const Muted = () => <span style={{ color: "#b6bacb" }}>—</span>;

/**
 * One customer with everything charged to it. A customer owns many cost
 * centers and many projects, so both are listed here rather than being
 * guessed at from the two separate list screens.
 */
const View = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await Http.get("/acc-customer/" + id);
        setCustomer(res.data);
      } catch (e) {
        console.error("Fetch error:", e);
        setError("Could not load the customer. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const costCenters = customer?.cost_centers || [];
  const projects = customer?.projects || [];
  const centerBudget = costCenters.reduce(
    (sum, row) => sum + num(row.budget_amount),
    0,
  );
  const projectBudget = projects.reduce(
    (sum, row) => sum + num(row.budget_amount),
    0,
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              {customer ? customer.name : "Customer"}{" "}
              {customer?.code && (
                <span className="coa-code">{customer.code}</span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              Cost centers and projects charged to this customer
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/acc-customer" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
            {customer && (
              <Link
                to={"/edit-acc-customer/" + customer.id}
                className="btn btn-primary"
              >
                <i className="fa fa-pencil mg-r-6"></i>Edit
              </Link>
            )}
          </div>
        </div>

        <div className="am-pagebody">
          {error && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            customer && (
              <>
                <div className="row mg-b-20">
                  <div className="col-md-3 mg-b-10">
                    <div className="coa-stat">
                      <i className="fa fa-flag-o coa-stat-icon"></i>
                      <span className="coa-stat-label">Opening Balance</span>
                      <span className="coa-stat-value">
                        {money(customer.opening_balance)}
                      </span>
                      <span className="coa-stat-sub">
                        {customer.effective_date
                          ? `From ${formatDate(customer.effective_date)}`
                          : "No effective date set"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-3 mg-b-10">
                    <div className="coa-stat">
                      <i className="fa fa-credit-card coa-stat-icon"></i>
                      <span className="coa-stat-label">Credit Limit</span>
                      <span className="coa-stat-value">
                        {money(customer.credit_limit)}
                      </span>
                      <span className="coa-stat-sub">Maximum outstanding</span>
                    </div>
                  </div>
                  <div className="col-md-3 mg-b-10">
                    <div className="coa-stat">
                      <i className="fa fa-bullseye coa-stat-icon"></i>
                      <span className="coa-stat-label">Cost Centers</span>
                      <span className="coa-stat-value">
                        {costCenters.length}
                      </span>
                      <span className="coa-stat-sub">
                        {money(centerBudget)} budgeted
                      </span>
                    </div>
                  </div>
                  <div className="col-md-3 mg-b-10">
                    <div className="coa-stat">
                      <i className="fa fa-briefcase coa-stat-icon"></i>
                      <span className="coa-stat-label">Projects</span>
                      <span className="coa-stat-value">{projects.length}</span>
                      <span className="coa-stat-sub">
                        {money(projectBudget)} budgeted
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-4">
                    <div className="coa-panel">
                      <div className="coa-panel-head">
                        <span>
                          <i className="fa fa-address-book-o me-2"></i>Contact
                        </span>
                        <StatusPill status={customer.status} />
                      </div>
                      <div className="coa-panel-body pd-t-6">
                        {[
                          ["Contact Person", customer.contact_person],
                          ["Phone", customer.phone],
                          ["Email", customer.email],
                          ["Address", customer.address],
                          ["Remarks", customer.description],
                        ].map(([label, value]) => (
                          <div className="coa-kv" key={label}>
                            <span className="coa-kv-label">{label}</span>
                            <span className="coa-kv-value">
                              {value || <Muted />}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-8">
                    <div className="coa-panel">
                      <div className="coa-panel-head">
                        <span>
                          <i className="fa fa-bullseye me-2"></i>Cost Centers
                        </span>
                        <Link
                          to="/create-cost-center"
                          className="btn btn-sm btn-outline-secondary"
                        >
                          <i className="fa fa-plus mg-r-6"></i>New Cost Center
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <table className="table coa-table">
                          <thead>
                            <tr>
                              <th>Code</th>
                              <th>Name</th>
                              <th>Type</th>
                              <th className="coa-num">Budget</th>
                              <th>Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {costCenters.length === 0 && (
                              <tr>
                                <td colSpan="6">
                                  <div className="coa-empty">
                                    <i className="fa fa-bullseye"></i>
                                    No cost centers for this customer yet.
                                  </div>
                                </td>
                              </tr>
                            )}
                            {costCenters.map((row) => (
                              <tr key={row.id}>
                                <td>
                                  <span className="coa-code">{row.code}</span>
                                </td>
                                <td
                                  style={{ fontWeight: 600, color: "#2a2962" }}
                                >
                                  {row.name}
                                </td>
                                <td>{capitalize(row.type)}</td>
                                <td className="coa-num">
                                  {money(row.budget_amount)}
                                </td>
                                <td>
                                  <StatusPill status={row.status} />
                                </td>
                                <td className="text-end">
                                  <Link
                                    to={"/edit-cost-center/" + row.id}
                                    className="btn btn-outline-secondary btn-sm"
                                    title="Edit cost center"
                                  >
                                    <i
                                      className="fa fa-pencil"
                                      aria-hidden="true"
                                    ></i>
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {costCenters.length > 0 && (
                            <tfoot>
                              <tr>
                                <th colSpan="3" className="coa-num">
                                  Total budget
                                </th>
                                <th className="coa-num">
                                  {money(centerBudget)}
                                </th>
                                <th colSpan="2"></th>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>

                    <div className="coa-panel">
                      <div className="coa-panel-head">
                        <span>
                          <i className="fa fa-briefcase me-2"></i>Projects
                        </span>
                        <Link
                          to={"/create-acc-project?customer_id=" + customer.id}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          <i className="fa fa-plus mg-r-6"></i>New Project
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <table className="table coa-table">
                          <thead>
                            <tr>
                              <th>Code</th>
                              <th>Name</th>
                              <th>Cost Center</th>
                              <th>Period</th>
                              <th className="coa-num">Budget</th>
                              <th>Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {projects.length === 0 && (
                              <tr>
                                <td colSpan="7">
                                  <div className="coa-empty">
                                    <i className="fa fa-briefcase"></i>
                                    No projects for this customer yet.
                                  </div>
                                </td>
                              </tr>
                            )}
                            {projects.map((row) => (
                              <tr key={row.id}>
                                <td>
                                  <span className="coa-code">{row.code}</span>
                                </td>
                                <td
                                  style={{ fontWeight: 600, color: "#2a2962" }}
                                >
                                  {row.name}
                                </td>
                                <td>
                                  {row.cost_center ? (
                                    `${row.cost_center.code} - ${row.cost_center.name}`
                                  ) : (
                                    <Muted />
                                  )}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {row.start_date || row.end_date ? (
                                    `${formatDate(row.start_date)} — ${formatDate(
                                      row.end_date,
                                    )}`
                                  ) : (
                                    <Muted />
                                  )}
                                </td>
                                <td className="coa-num">
                                  {money(row.budget_amount)}
                                </td>
                                <td>
                                  <StatusPill status={row.status} />
                                </td>
                                <td className="text-end">
                                  <Link
                                    to={"/edit-acc-project/" + row.id}
                                    className="btn btn-outline-secondary btn-sm"
                                    title="Edit project"
                                  >
                                    <i
                                      className="fa fa-pencil"
                                      aria-hidden="true"
                                    ></i>
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {projects.length > 0 && (
                            <tfoot>
                              <tr>
                                <th colSpan="4" className="coa-num">
                                  Total budget
                                </th>
                                <th className="coa-num">
                                  {money(projectBudget)}
                                </th>
                                <th colSpan="2"></th>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default View;
